import React from 'react';
import {API_BASE_URL} from "../../config";

const Api = () => {
  // Core request method
  const request = async (url: string, options: RequestInit = {}, isJson = true) => {
    const accessToken = localStorage.getItem('access_token');
    const headers: HeadersInit = {
      ...(isJson && { 'Content-Type': 'application/json' }),
      ...(accessToken ? { 'X-Auth': accessToken } : {}),
      ...options.headers,
    };

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, { ...options, headers });

      if (response.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/signin';
        throw new Error('Unauthorized. Redirecting to login.');
      }

      if (!response.ok) {
        throw await response.json();
      }

      return response;
    } catch (err: any) {
      console.error('Error in request:', err);
      throw err;
    }
  };

  const errorHandler = (resp: ApiResponse | any, toasts: any) => {
    if (resp && resp.status && Array.isArray(resp.errors)) {
      resp.errors.forEach((error: string) => {
        toasts.danger({
          headerContent: <strong className={'me-auto'}>Error</strong>,
          bodyContent: error,
          toastProps: {
            className: 'colored-toast bg-danger text-white',
          },
          toastHeaderProps: {
            className: 'bg-danger text-white',
          },
        });
      });
    } else {
      toasts.danger({
        headerContent: <strong className={'me-auto'}>Error</strong>,
        bodyContent: resp.message || 'An unexpected error occurred',
        toastProps: {
          className: 'colored-toast bg-danger text-white',
        },
        toastHeaderProps: {
          className: 'bg-danger text-white',
        },
      });
    }
  };

  const getFiles = async (page: number, limit: number, order: string, search: string) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      order,
      ...(search ? { search } : {}),
    }).toString();

    const response = await request(`/files?${params}`);

    return response.json() as Promise<ApiResponse<UploadedFile[]>>;
  };

  const deleteFile = async (fileId: string) => {
    return request(`/files/${fileId}`, { method: 'DELETE' });
  };

  const downloadFile = async (fileId: string) => {
    const response = await request(`/files/${fileId}/download`, { method: 'GET' });

    return response.arrayBuffer();
  };

  const updloadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('filepond', file);

    await request('/files/upload', { body: formData, method: 'POST' }, false);
  };

  const downloadBlob = async (buffer: ArrayBuffer, fileName: string) => {
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');

    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const logout = async () => {
    const response = await request('/auth/sign-out', {
      method: 'POST',
    });

    return response;
  };

  const getProfile = async () => {
    const response = await request('/profile');

    return response.json();
  };

  return {
    request,
    errorHandler,
    getFiles,
    deleteFile,
    downloadFile,
    updloadFile,
    downloadBlob,
    logout,
    getProfile,
  };
};

export default Api;
